// ServiceNow Tickets — incident/request/change analytics, plus the raw ingest feed

import { pbAuthedFetch } from '../authHeaders';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';

/* ── helpers ─────────────────────────────────────────────── */

function formatHours(h: number | null | undefined): string {
  if (h == null || isNaN(h)) return '—';
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

const PRIORITY_COLORS: Record<number, string> = {
  1: 'var(--risk)',
  2: 'var(--state-conflict)',
  3: 'var(--gold)',
  4: 'var(--ok)',
  5: 'var(--ink-45)',
};

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Planning',
};

const tooltipStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--hair)',
  borderRadius: 'var(--r-ctl)',
  fontSize: 'var(--text-sm)',
};

const tickStyle = { fontSize: 'var(--text-xs)', fill: 'var(--ink-45)' };

/* ── sort helper ─────────────────────────────────────────── */

type SortDir = 'asc' | 'desc';

function useSortable<T>(data: T[], defaultKey: keyof T) {
  const [sortKey, setSortKey] = useState<keyof T>(defaultKey);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggle = (key: keyof T) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, toggle, sortKey, sortDir };
}

function SortHeader({
  label,
  field,
  sortKey,
  sortDir,
  onToggle,
  num,
}: {
  label: string;
  field: string;
  sortKey: string;
  sortDir: SortDir;
  onToggle: (f: string) => void;
  num?: boolean;
}) {
  const active = field === sortKey;
  return (
    <th>
      <button
        className={`th${num ? ' th--num' : ''}`}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
        onClick={() => onToggle(field)}
      >
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </button>
    </th>
  );
}

/* ── feed types & fetcher ────────────────────────────────── */

interface FeedConfigResp { ok: boolean; connected: boolean; instance_url: string | null; tables: string[]; refresh_cadence: string }
interface FeedIncident { id: string; number: string; priority: number; short_description: string; assignment_group: string; attributed_to: string | null; age_hours: number; state: string }
interface FeedChange { id: string; number: string; short_description: string; owner: string | null; status: string; cab_required: boolean; scheduled_at: string }

async function fetchJson<T>(url: string): Promise<T> {
  const res = await pbAuthedFetch(url);
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
}

/* ── analytics panel ─────────────────────────────────────── */

function AnalyticsPanel() {
  const { data: summary, isLoading: sumLoad, isError: sumErr, refetch: refetchSummary } = useQuery({
    queryKey: ['servicenow-analytics-summary'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/servicenow-analytics/summary');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const { data: trends } = useQuery({
    queryKey: ['servicenow-analytics-trends'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/servicenow-analytics/trends');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const { data: priorityDist } = useQuery({
    queryKey: ['servicenow-analytics-priority'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/servicenow-analytics/priority-distribution');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const { data: categoryData } = useQuery({
    queryKey: ['servicenow-analytics-categories'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/servicenow-analytics/category-breakdown');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const { data: resolutionData } = useQuery({
    queryKey: ['servicenow-analytics-resolution'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/servicenow-analytics/resolution-times');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });

  /* derived values */
  const totalTickets = summary?.total_tickets ?? 0;
  const openTickets = summary?.open_tickets ?? 0;
  const avgResolution = summary?.avg_resolution_hours ?? null;
  const thisMonth = summary?.tickets_this_month ?? 0;

  const trendSeries = trends?.series ?? [];
  const priorityBars = priorityDist?.priorities ?? [];
  const categories = categoryData?.categories ?? [];
  const topCategories = categories.slice(0, 15);

  const resolution = resolutionData ?? {};
  const medianH = resolution.median_hours ?? null;
  const p90H = resolution.p90_hours ?? null;
  const p95H = resolution.p95_hours ?? null;
  const resolutionByPriority = resolution.by_priority ?? [];
  const resolutionByCategory = (resolution.by_category ?? []).slice(0, 10);

  /* category table sort */
  const { sorted: sortedCats, toggle: toggleCat, sortKey: catKey, sortDir: catDir } =
    useSortable<{ category: string; total: number; open: number; avg_resolution_hours: number }>(
      topCategories as any,
      'total'
    );

  /* resolution-by-category table sort */
  const {
    sorted: sortedResCats,
    toggle: toggleResCat,
    sortKey: resKey,
    sortDir: resDir,
  } = useSortable<{ category: string; median_hours: number; p90_hours: number; p95_hours: number }>(
    resolutionByCategory as any,
    'category'
  );

  if (sumLoad) {
    return <div className="page-loading" role="status" aria-live="polite">Loading analytics…</div>;
  }

  if (sumErr) {
    return (
      <div className="state state--risk">
        <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h3 className="state__title">Failed to load ServiceNow analytics</h3>
        <p className="state__body">
          Could not connect to the analytics API. Please check your connection and try again.
        </p>
        <button className="btn btn--primary" onClick={() => refetchSummary()}>
          Retry
        </button>
      </div>
    );
  }

  const openHigh = openTickets > totalTickets * 0.4;
  const openRatio = totalTickets > 0 ? Math.round((openTickets / totalTickets) * 100) : 0;

  return (
    <>
      {/* ── high open ratio notice ──────────────────── */}
      {openHigh && (
        <div className="notice notice--risk">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div>
            <p className="notice__title">High Open Ticket Ratio</p>
            <p className="notice__body">
              <strong>{openRatio}%</strong> of tickets are currently open ({openTickets.toLocaleString()} of {totalTickets.toLocaleString()}). This exceeds the 40% threshold.
            </p>
          </div>
        </div>
      )}

      {/* ── summary indicators ──────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Ticket summary">
        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Total</p>
            <p className="ind__full">All Tickets</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{totalTickets.toLocaleString()}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--ok)' }}></i>
            <b>Cumulative</b> — all tracked tickets
          </p>
        </div>

        <div className={`ind ${openHigh ? 'ind--breach' : 'ind--watch'}`}>
          <div className="ind__head">
            <p className="ind__abbr">Open</p>
            <p className="ind__full">Currently Open</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{openTickets.toLocaleString()}</p>
            {openHigh && (
              <span className="ind__delta ind__delta--bad">↑ {openRatio}%</span>
            )}
          </div>
          <p className="ind__reading">
            <i style={{ background: openHigh ? 'var(--risk)' : 'var(--gold)' }}></i>
            <b style={{ color: openHigh ? 'var(--risk)' : 'var(--gold)' }}>
              {openHigh ? 'Action required' : 'Monitor'}
            </b> — {openHigh ? 'exceeds 40% threshold' : 'within normal range'}
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Avg Res.</p>
            <p className="ind__full">Avg Resolution Time</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{formatHours(avgResolution)}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--ok)' }}></i>
            <b>Mean time</b> — across all resolved tickets
          </p>
        </div>

        <div className="ind">
          <div className="ind__head">
            <p className="ind__abbr">Month</p>
            <p className="ind__full">Tickets This Month</p>
          </div>
          <div className="ind__value">
            <p className="ind__v">{thisMonth.toLocaleString()}</p>
          </div>
          <p className="ind__reading">
            <i style={{ background: 'var(--gold)' }}></i>
            <b>Current period</b> — new tickets opened
          </p>
        </div>
      </div>

      {/* ── ticket volume trend ─────────────────────── */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Trends</p>
            <h2 className="box__title">Ticket Volume — Last 30 Days</h2>
          </div>
        </div>
        <div className="box__body">
          {trendSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={trendSeries}>
                <defs>
                  <linearGradient id="openedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--gold)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--gold)" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="resolvedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--ok)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--ok)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                <XAxis
                  dataKey="date"
                  tick={tickStyle}
                  tickFormatter={(v: any) => {
                    const d = new Date(v);
                    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis yAxisId="left" tick={tickStyle} />
                <YAxis yAxisId="right" orientation="right" tick={tickStyle} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(v: any) =>
                    new Date(v).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
                  }
                />
                <Legend wrapperStyle={{ fontSize: 'var(--text-sm)' }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="opened"
                  name="Opened"
                  stroke="var(--gold)"
                  fill="url(#openedGrad)"
                  strokeWidth={2}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="resolved"
                  name="Resolved"
                  stroke="var(--ok)"
                  fill="url(#resolvedGrad)"
                  strokeWidth={2}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="avg_resolution_hours"
                  name="Avg Resolution (h)"
                  stroke="var(--risk)"
                  fill="none"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <h3 className="state__title">No trend data</h3>
              <p className="state__body">Ticket volume data will appear here once available.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── priority distribution ───────────────────── */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Breakdown</p>
            <h2 className="box__title">Priority Distribution</h2>
          </div>
        </div>
        <div className="box__body">
          {priorityBars.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={priorityBars} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                <XAxis type="number" tick={tickStyle} />
                <YAxis
                  type="category"
                  dataKey="priority"
                  tick={tickStyle}
                  width={80}
                  tickFormatter={(v: number) => `P${v} — ${PRIORITY_LABELS[v] ?? ''}`}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 'var(--text-sm)' }} />
                <Bar dataKey="open" name="Open" stackId="a" radius={[0, 0, 0, 0]}>
                  {priorityBars.map((entry: any, i: number) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.priority] ?? 'var(--ink-45)'} opacity={0.85} />
                  ))}
                </Bar>
                <Bar dataKey="closed" name="Closed" stackId="a" radius={[0, 4, 4, 0]}>
                  {priorityBars.map((entry: any, i: number) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.priority] ?? 'var(--ink-45)'} opacity={0.45} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              <h3 className="state__title">No priority data</h3>
              <p className="state__body">Priority distribution will appear once ticket data is available.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── category breakdown ──────────────────────── */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Categories</p>
            <h2 className="box__title">Top 15 Categories</h2>
          </div>
        </div>
        <div className="box__body box__body--flush">
          {sortedCats.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <SortHeader label="Category" field="category" sortKey={catKey as string} sortDir={catDir} onToggle={toggleCat as any} />
                    <SortHeader label="Total" field="total" sortKey={catKey as string} sortDir={catDir} onToggle={toggleCat as any} num />
                    <SortHeader label="Open" field="open" sortKey={catKey as string} sortDir={catDir} onToggle={toggleCat as any} num />
                    <SortHeader label="Avg Resolution" field="avg_resolution_hours" sortKey={catKey as string} sortDir={catDir} onToggle={toggleCat as any} num />
                  </tr>
                </thead>
                <tbody>
                  {sortedCats.map((row: any, i: number) => (
                    <tr key={row.category ?? i}>
                      <td className="td--name">{row.category}</td>
                      <td className="td--num">{row.total?.toLocaleString()}</td>
                      <td className="td--num">{row.open?.toLocaleString()}</td>
                      <td className="td--num">{formatHours(row.avg_resolution_hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              <h3 className="state__title">No category data</h3>
              <p className="state__body">Category breakdown will appear once ticket data is available.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── resolution time analytics ───────────────── */}
      <div className="box">
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Performance</p>
            <h2 className="box__title">Resolution Time Analytics</h2>
          </div>
        </div>
        <div className="box__body">
          {/* resolution time indicators */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
            <div className="ind">
              <div className="ind__head">
                <p className="ind__abbr">Median</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{formatHours(medianH)}</p>
              </div>
            </div>
            <div className="ind">
              <div className="ind__head">
                <p className="ind__abbr">P90</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{formatHours(p90H)}</p>
              </div>
            </div>
            <div className="ind">
              <div className="ind__head">
                <p className="ind__abbr">P95</p>
              </div>
              <div className="ind__value">
                <p className="ind__v">{formatHours(p95H)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* resolution by priority chart */}
        <div className="box__body" style={{ borderTop: '1px solid var(--hair)' }}>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
            Resolution Time by Priority
          </p>
          {resolutionByPriority.length > 0 && resolutionByPriority.some((row: any) => Number(row.avg_hours) > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={resolutionByPriority}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                <XAxis
                  dataKey="priority"
                  tick={tickStyle}
                  tickFormatter={(v: number) => `P${v}`}
                />
                <YAxis tick={tickStyle} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: any) => formatHours(value)}
                />
                <Bar dataKey="avg_hours" name="Avg Hours" radius={[4, 4, 0, 0]}>
                  {resolutionByPriority.map((entry: any, i: number) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.priority] ?? 'var(--ink-45)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              <h3 className="state__title">No resolution data</h3>
              <p className="state__body">Resolution time by priority will appear once ticket data is available.</p>
            </div>
          )}
        </div>

        {/* resolution by category table */}
        <div className="box__body box__body--flush" style={{ borderTop: '1px solid var(--hair)' }}>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', letterSpacing: '.08em', textTransform: 'uppercase', padding: 'var(--sp-3) var(--sp-4) 0' }}>
            Resolution Time by Top 10 Categories
          </p>
          {sortedResCats.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <SortHeader label="Category" field="category" sortKey={resKey as string} sortDir={resDir} onToggle={toggleResCat as any} />
                    <SortHeader label="Median" field="median_hours" sortKey={resKey as string} sortDir={resDir} onToggle={toggleResCat as any} num />
                    <SortHeader label="P90" field="p90_hours" sortKey={resKey as string} sortDir={resDir} onToggle={toggleResCat as any} num />
                    <SortHeader label="P95" field="p95_hours" sortKey={resKey as string} sortDir={resDir} onToggle={toggleResCat as any} num />
                  </tr>
                </thead>
                <tbody>
                  {sortedResCats.map((row: any, i: number) => (
                    <tr key={row.category ?? i}>
                      <td className="td--name">{row.category}</td>
                      <td className="td--num">{formatHours(row.median_hours)}</td>
                      <td className="td--num">{formatHours(row.p90_hours)}</td>
                      <td className="td--num">{formatHours(row.p95_hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="state">
              <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <h3 className="state__title">No resolution data</h3>
              <p className="state__body">Resolution time data will appear once tickets are resolved.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ── feed panel ──────────────────────────────────────────── */

function FeedPanel() {
  const { data: config, isLoading: configLoading, error: configError } = useQuery<FeedConfigResp>({
    queryKey: ['servicenow-feed-config'],
    queryFn: () => fetchJson('/api/servicenow-feed/config'),
  });
  const { data: incidentsData } = useQuery<{ ok: boolean; incidents: FeedIncident[] }>({
    queryKey: ['servicenow-feed-incidents'],
    queryFn: () => fetchJson('/api/servicenow-feed/incidents'),
  });
  const { data: changesData } = useQuery<{ ok: boolean; changes: FeedChange[] }>({
    queryKey: ['servicenow-feed-changes'],
    queryFn: () => fetchJson('/api/servicenow-feed/changes'),
  });

  const incidentsRaw = incidentsData?.incidents || [];
  const changesRaw = changesData?.changes || [];
  const { sorted: incidents, toggle: toggleIncSort, sortKey: incSortKey, sortDir: incSortDir } =
    useSortable<FeedIncident>(incidentsRaw, 'number');
  const { sorted: changes, toggle: toggleChgSort, sortKey: chgSortKey, sortDir: chgSortDir } =
    useSortable<FeedChange>(changesRaw, 'number');

  if (configLoading) {
    return <div className="page-loading" role="status" aria-live="polite">Loading ServiceNow feed…</div>;
  }

  if (configError || !config) {
    return (
      <div className="state state--risk">
        <h3 className="state__title">Failed to load ServiceNow feed</h3>
        <p className="state__body">Could not connect to the ServiceNow feed API.</p>
      </div>
    );
  }

  return (
    <>
      <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%' }}>
          <span className={`pill ${config.connected ? 'pill--ok' : 'pill--warn'}`}>
            <i></i> {config.connected ? 'Connected' : 'Not connected'}
          </span>
          <p className="notice__body" style={{ margin: 0 }}>Read-only delta ingest · no writes to the system of record</p>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn--secondary btn--sm" onClick={() => window.askStella?.('What change requests need a CAB decision this week?')}>Ask Stella about this</button>
          </div>
        </div>
      </div>

      <div className="box" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="box__head"><h3 className="box__title">Ingest configuration</h3></div>
        <div className="box__body" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-4)' }}>
          <div><p className="ind__abbr">Tables</p><p>{config.tables.join(' · ')}</p></div>
          <div><p className="ind__abbr">Refresh</p><p>{config.refresh_cadence}</p></div>
          <div><p className="ind__abbr">Instance</p><p>{config.instance_url || '—'}</p></div>
          <div><p className="ind__abbr">Writes</p><p>None — read-only credentials</p></div>
        </div>
      </div>

      <div className="box" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="box__head"><h3 className="box__title">Incident queue</h3></div>
        <div className="box__body">
          <table className="tbl">
            <thead>
              <tr>
                <SortHeader label="Number" field="number" sortKey={incSortKey as string} sortDir={incSortDir} onToggle={(f) => toggleIncSort(f as keyof FeedIncident)} />
                <SortHeader label="Pri" field="priority" sortKey={incSortKey as string} sortDir={incSortDir} onToggle={(f) => toggleIncSort(f as keyof FeedIncident)} num />
                <SortHeader label="Short description" field="short_description" sortKey={incSortKey as string} sortDir={incSortDir} onToggle={(f) => toggleIncSort(f as keyof FeedIncident)} />
                <th><button className="th th--static">Assignment group</button></th>
                <th><button className="th th--static">Attributed to</button></th>
                <SortHeader label="Age" field="age_hours" sortKey={incSortKey as string} sortDir={incSortDir} onToggle={(f) => toggleIncSort(f as keyof FeedIncident)} num />
                <th><button className="th th--static">State</button></th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => (
                <tr key={inc.id}>
                  <td className="td--id">{inc.number}</td>
                  <td className="td--num">P{inc.priority}</td>
                  <td className="td--name">{inc.short_description}</td>
                  <td>{inc.assignment_group || '—'}</td>
                  <td>{inc.attributed_to || '—'}</td>
                  <td className="td--num">{inc.age_hours}h</td>
                  <td>{inc.state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="box">
        <div className="box__head"><h3 className="box__title">Change requests</h3></div>
        <div className="box__body">
          <table className="tbl">
            <thead>
              <tr>
                <SortHeader label="Number" field="number" sortKey={chgSortKey as string} sortDir={chgSortDir} onToggle={(f) => toggleChgSort(f as keyof FeedChange)} />
                <SortHeader label="Change" field="short_description" sortKey={chgSortKey as string} sortDir={chgSortDir} onToggle={(f) => toggleChgSort(f as keyof FeedChange)} />
                <th><button className="th th--static">Owner</button></th>
                <th><button className="th th--static">Status</button></th>
                <th><button className="th th--static">Date</button></th>
              </tr>
            </thead>
            <tbody>
              {changes.map((chg) => (
                <tr key={chg.id}>
                  <td className="td--id">{chg.number}</td>
                  <td className="td--name">{chg.short_description}</td>
                  <td>{chg.owner || '—'}</td>
                  <td>
                    <span className={`pill ${chg.status === 'closed' ? 'pill--ok' : 'pill--warn'}`}>
                      <i></i> {chg.status}{chg.cab_required ? ' · CAB' : ''}
                    </span>
                  </td>
                  <td>{new Date(chg.scheduled_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ── page ────────────────────────────────────────────────── */

type SNTab = 'analytics' | 'feed';

function initialTab(): SNTab {
  try {
    return new URLSearchParams(window.location.search).get('tab') === 'feed' ? 'feed' : 'analytics';
  } catch {
    return 'analytics';
  }
}

export function ServiceNowAnalyticsPage() {
  const [tab, setTab] = useState<SNTab>(initialTab);

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Analytics</p>
            <h1 className="pagehead__title">ServiceNow Tickets</h1>
            <div className="pagehead__facts">
              <span className="fact">Incident, request, and change management</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="tabs" role="tablist" aria-label="ServiceNow view">
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'analytics'}
            onClick={() => setTab('analytics')}
          >
            Analytics
          </button>
          <button
            className="tab"
            role="tab"
            aria-selected={tab === 'feed'}
            onClick={() => setTab('feed')}
          >
            Feed
          </button>
        </div>

        {tab === 'analytics' ? <AnalyticsPanel /> : <FeedPanel />}
      </div>
    </>
  );
}
